import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Send,
  Schedule,
  SportsSoccer,
  Edit,
} from '@mui/icons-material';

import { useCanManageTeam, usePermissions } from '../contexts/PermissionsContext';
import { ReportDraft, StatusResponse } from '../types/matchReport.ts';
import {
  MatchReportAPI,
} from '../utils/matchReportApi.ts';
import unifiedApi from '../utils/unifiedApi';

import ReconciliationBanner from '../components/MatchReport/ReconciliationBanner.tsx';
import ScoreStep from '../components/MatchReport/ScoreStep.tsx';
import AppearancesStep from '../components/MatchReport/AppearancesStep.tsx';
import EventsStep from '../components/MatchReport/EventsStep.tsx';
import ReviewStep from '../components/MatchReport/ReviewStep.tsx';

const steps = [
  { label: 'Score', description: 'Enter the final score' },
  { label: 'Players', description: 'Select who played' },
  { label: 'Events', description: 'Record goals and cards' },
  { label: 'Review', description: 'Confirm and submit' },
];

function getOpponentReportScore(
  status: StatusResponse | null,
  editMode: boolean,
): { homeGoals: number; awayGoals: number } | null {
  if (
    !editMode &&
    status?.opponentReport?.status === 'Submitted' &&
    status.opponentReport.homeGoals !== undefined &&
    status.opponentReport.awayGoals !== undefined
  ) {
    return {
      homeGoals: status.opponentReport.homeGoals,
      awayGoals: status.opponentReport.awayGoals,
    };
  }
  return null;
}

export const SubmitMatchReport: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { teamId, gameId } = useParams<{
    teamId: string;
    gameId: string;
  }>();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isEditMode = location.pathname.endsWith('/edit');
  const canManageTeam = useCanManageTeam(parseInt(teamId || '0'));
  const { canManageLeague } = usePermissions();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editReportId, setEditReportId] = useState<number | null>(null);
  const [editLeagueId, setEditLeagueId] = useState<number | null>(null);
  const [draft, setDraft] = useState<ReportDraft>({
    homeGoals: null,
    awayGoals: null,
    appearances: [],
    events: [],
    notes: '',
    isForfeit: false,
  });
  const [gameStatus, setGameStatus] = useState<StatusResponse | null>(null);
  const [gameInfo, setGameInfo] = useState<{
    homeTeamName: string;
    awayTeamName: string;
    isHomeTeam: boolean;
    gameDate: string;
  } | null>(null);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      if (!gameId || !teamId) {
        setError('Missing game or team information');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Load game status
        const status = await MatchReportAPI.fetchStatus(parseInt(gameId), parseInt(teamId || '0'));
        setGameStatus(status);
        
        // Extract game info
        if (status.gameInfo) {
          setGameInfo({
            homeTeamName: status.gameInfo.homeTeamName,
            awayTeamName: status.gameInfo.awayTeamName,
            isHomeTeam: status.gameInfo.isHomeTeam,
            gameDate: status.gameInfo.gameDate,
          });
        }

        // Pre-populate score from opponent's report if they've already submitted
        const opponentScore = getOpponentReportScore(status, isEditMode);
        if (opponentScore) {
          setDraft(prev => ({
            ...prev,
            homeGoals: opponentScore.homeGoals,
            awayGoals: opponentScore.awayGoals,
          }));
        }

        // In edit mode, fetch and populate the existing report
        if (isEditMode) {
          const reportData = await unifiedApi.get(`/api/games/${gameId}/teams/${teamId}/report`);
          if (!reportData) {
            setError('No submitted match report found for this team');
            return;
          }

          // Store report ID and league ID for the update call
          const summaryData = await unifiedApi.get(`/api/games/${gameId}/reports`);
          const isHomeTeam = summaryData?.homeTeamId === parseInt(teamId);
          const teamReport = isHomeTeam ? summaryData?.homeTeamReport : summaryData?.awayTeamReport;
          if (teamReport) {
            setEditReportId(teamReport.id);
            setEditLeagueId(teamReport.leagueId);
          }

          // Parse the PayloadJson from the report DTO to populate the draft
          if (teamReport?.payloadJson) {
            try {
              const payload = JSON.parse(teamReport.payloadJson);
              
              // Fetch team roster to merge with payload appearances
              const roster = await MatchReportAPI.fetchTeamRoster(parseInt(teamId));
              
              // Build appearances from payload, matching with roster for display names
              const rosterMap = new Map(roster.map((p: any) => [p.userTenantId, p]));
              const playedUserIds = new Set((payload.appearances || []).map((a: any) => a.userTenantId));
              
              const appearances = [
                // Players who played (from payload)
                ...(payload.appearances || []).map((a: any) => {
                  const rosterPlayer = rosterMap.get(a.userTenantId);
                  return {
                    userTenantId: a.userTenantId,
                    isSpare: a.isSpare || false,
                    shirtNumber: a.shirtNumber?.toString() || '',
                    played: true,
                    firstName: rosterPlayer?.firstName || '',
                    lastName: rosterPlayer?.lastName || '',
                    playerName: rosterPlayer?.name || '',
                    position: rosterPlayer?.position || (a.isSpare ? 'Spare' : ''),
                  };
                }),
                // Roster players who did NOT play
                ...roster
                  .filter((p: any) => !playedUserIds.has(p.userTenantId))
                  .map((p: any) => ({
                    userTenantId: p.userTenantId,
                    isSpare: false,
                    shirtNumber: p.shirtNumber?.toString() || '',
                    played: false,
                    firstName: p.firstName || '',
                    lastName: p.lastName || '',
                    playerName: p.name || '',
                    position: p.position || '',
                  })),
              ];

              // Build events from payload (goals, cards, other events)
              const events: any[] = [];
              
              (payload.scorers || []).forEach((s: any) => {
                events.push({
                  id: crypto.randomUUID(),
                  eventType: 'Goal',
                  userTenantId: s.userTenantId,
                  relatedUserTenantId: s.assistUserTenantId || undefined,
                  minute: s.minute,
                  notes: s.notes,
                });
              });

              (payload.cards || []).forEach((c: any) => {
                events.push({
                  id: crypto.randomUUID(),
                  eventType: c.cardType,
                  userTenantId: c.userTenantId,
                  minute: c.minute,
                  notes: c.notes,
                });
              });

              (payload.otherEvents || []).forEach((e: any) => {
                events.push({
                  id: crypto.randomUUID(),
                  eventType: e.eventType,
                  userTenantId: e.userTenantId,
                  minute: e.minute,
                  notes: e.notes,
                });
              });

              setDraft({
                homeGoals: payload.homeGoals ?? null,
                awayGoals: payload.awayGoals ?? null,
                appearances,
                events,
                notes: payload.notes || '',
                isForfeit: false,
              });
            } catch (parseErr) {
              console.error('Failed to parse report payload:', parseErr);
              setError('Failed to load existing report data');
            }
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load game data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [gameId, teamId, isEditMode]);

  const handleDraftChange = useCallback((updates: Partial<ReportDraft>) => {
    setDraft((prev: ReportDraft) => ({ ...prev, ...updates }));
  }, []);

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!gameId || !teamId) return;

    try {
      setSubmitting(true);
      setError(null);
      
      let status: StatusResponse;
      if (isEditMode && editReportId) {
        status = await MatchReportAPI.editReport(parseInt(gameId), editReportId, draft, parseInt(teamId));
      } else {
        status = await MatchReportAPI.submitReport(parseInt(gameId), draft, parseInt(teamId));
      }
      setGameStatus(status);
      
      // Navigate back to team page using the team slug or ID
      navigate(`/teams/${teamId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : isEditMode ? 'Failed to update report' : 'Failed to submit report');
    } finally {
      setSubmitting(false);
      setConfirmSubmitOpen(false);
    }
  };

  const refreshStatus = async () => {
    if (!gameId) return;

    try {
      const status = await MatchReportAPI.fetchStatus(parseInt(gameId), parseInt(teamId || '0'));
      setGameStatus(status);
    } catch (err) {
      console.warn('Failed to refresh status:', err);
    }
  };

  // Helper function to get current team name
  const getCurrentTeamName = () => {
    if (!gameInfo || !teamId) return 'Unknown Team';
    
    const currentTeamId = parseInt(teamId);
    // Use the homeTeamId and awayTeamId from the status if available
    if (gameStatus?.gameInfo) {
      // Check if we can access the team IDs from the status response
      const statusData = gameStatus as any;
      if (statusData.homeTeamId === currentTeamId) {
        return gameInfo.homeTeamName;
      } else if (statusData.awayTeamId === currentTeamId) {
        return gameInfo.awayTeamName;
      }
    }
    
    // Fallback to isHomeTeam logic
    return gameInfo.isHomeTeam ? gameInfo.homeTeamName : gameInfo.awayTeamName;
  };

  const canContinue = () => {
    switch (activeStep) {
      case 0: // Score step
        return !draft.isForfeit ? 
          (draft.homeGoals !== null && draft.awayGoals !== null && 
           draft.homeGoals >= 0 && draft.awayGoals >= 0) : 
          true;
      case 1: // Players step
        return draft.appearances.filter((app: any) => app.played).length > 0;
      case 2: { // Events step
        if (draft.isForfeit) return true;
        if (!gameInfo) return true;
        const scoresSet = draft.homeGoals !== null && draft.awayGoals !== null;
        if (!scoresSet) return true;
        const goalCount = draft.events.filter((e: any) => e.eventType === 'Goal').length;
        const myTeamGoals = gameInfo.isHomeTeam ? (draft.homeGoals ?? 0) : (draft.awayGoals ?? 0);
        const opponentGoals = gameInfo.isHomeTeam ? (draft.awayGoals ?? 0) : (draft.homeGoals ?? 0);
        if (myTeamGoals !== goalCount) return false;
        if (opponentGoals === 0 && !draft.events.some((e: any) => e.eventType === 'CleanSheet')) return false;
        return true;
      }
      case 3: // Review step
        return draft.appearances.filter((app: any) => app.played).length > 0;
      default:
        return false;
    }
  };

  const canSubmit = () => {
    return canContinue() && activeStep === steps.length - 1;
  };

  const hasPermission = isEditMode 
    ? (editLeagueId ? canManageLeague(editLeagueId) : false) 
    : canManageTeam;

  if (!loading && !hasPermission) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          You don't have permission to manage this team's match reports.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading match information...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Container>
    );
  }

  const renderStepContent = () => {
    if (!gameInfo) return null;

    switch (activeStep) {
      case 0:
        return (
          <ScoreStep
            draft={draft}
            onChange={handleDraftChange}
            homeTeamName={gameInfo.homeTeamName}
            awayTeamName={gameInfo.awayTeamName}
            isHomeTeam={gameInfo.isHomeTeam}
            opponentReportScore={getOpponentReportScore(gameStatus, isEditMode)}
          />
        );
      case 1:
        return (
          <AppearancesStep
            draft={draft}
            onChange={handleDraftChange}
            teamId={parseInt(teamId || '0')}
            gameId={parseInt(gameId || '0')}
          />
        );
      case 2:
        return (
          <EventsStep
            draft={draft}
            onChange={handleDraftChange}
            isHomeTeam={gameInfo?.isHomeTeam}
          />
        );
      case 3:
        return (
          <ReviewStep
            draft={draft}
            homeTeamName={gameInfo.homeTeamName}
            awayTeamName={gameInfo.awayTeamName}
            isHomeTeam={gameInfo.isHomeTeam}
            gameDate={gameInfo.gameDate}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
     {/* Game Information Header */}
      {gameInfo && (
        <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
          <CardContent>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                {isEditMode ? <Edit /> : <Schedule />}
                {isEditMode ? 'Edit Match Report' : 'Match Report Submission'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {new Date(gameInfo.gameDate).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.5, sm: 2 }, mb: 2 }}>
                <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1.25rem' }, fontWeight: 600 }}>{gameInfo.homeTeamName}</Typography>
                <Typography variant="h5" sx={{ fontSize: { xs: '1rem', sm: '1.5rem' } }}>vs</Typography>
                <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1.25rem' }, fontWeight: 600 }}>{gameInfo.awayTeamName}</Typography>
              </Box>
              <Chip
                icon={<SportsSoccer />}
                label={`Submitting for: ${getCurrentTeamName()}`}
                clickable={false}
                onClick={() => {}}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'inherit',
                  '& .MuiChip-icon': { color: 'inherit' }
                }}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Status Banner */}
      {gameStatus && (
        <Box sx={{ mb: 3 }}>
          <ReconciliationBanner status={gameStatus} onRefresh={refreshStatus} />
        </Box>
      )}

      {/* Stepper */}
      <Paper sx={{ p: { xs: 1.5, sm: 3 }, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((step) => (
            <Step key={step.label}>
              <StepLabel>
                <Typography variant="body2" fontWeight="medium">
                  {step.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                  {step.description}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Step Content */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, minHeight: 400 }}>
        {renderStepContent()}
      </Paper>

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button
          onClick={handleBack}
          disabled={activeStep === 0}
          startIcon={<ArrowBack />}
          fullWidth={isMobile}
        >
          Back
        </Button>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: isMobile ? '100%' : 'auto' }}>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={() => setConfirmSubmitOpen(true)}
              disabled={!canSubmit() || submitting}
              startIcon={submitting ? <CircularProgress size={16} /> : <Send />}
              fullWidth={isMobile}
            >
              {submitting ? (isEditMode ? 'Saving...' : 'Submitting...') : (isEditMode ? 'Save Changes' : 'Submit Report')}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!canContinue()}
              endIcon={<ArrowForward />}
              fullWidth={isMobile}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>

      {/* Confirm Submit Dialog */}
      <Dialog open={confirmSubmitOpen} onClose={() => setConfirmSubmitOpen(false)}>
        <DialogTitle>{isEditMode ? 'Confirm Changes' : 'Confirm Submission'}</DialogTitle>
        <DialogContent>
          {isEditMode ? (
            <>
              <Typography gutterBottom>
                Are you sure you want to save these changes? This will:
              </Typography>
              <Typography component="ul" sx={{ pl: 2 }}>
                <li>Replace the existing report data with the updated values</li>
                <li>Re-process player appearances, goals, cards, and events</li>
                <li>Reverse and recalculate standings if the game was finalized</li>
              </Typography>
              <Alert severity="warning" sx={{ mt: 2 }}>
                If this game's score was already finalized in the standings, updating the report may change the league standings.
              </Alert>
            </>
          ) : (
            <Typography>
              Are you sure you want to submit this match report? Once submitted, you may not be able to make changes.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSubmitOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? (isEditMode ? 'Saving...' : 'Submitting...') : (isEditMode ? 'Save Changes' : 'Submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SubmitMatchReport;